/**
 * LOOW — Ansøgningsformular: Typing Tracker
 * 
 * Dette script overvåger brugerens adfærd i formularfelter for at vurdere
 * om indholdet er skrevet manuelt eller copy-pastet (mulig AI-brug).
 * 
 * SÅDAN BRUGER DU SCRIPTET:
 * 1. Tilføj dette script til din side (enten inline i <script> eller som ekstern fil)
 * 2. Giv dine formularfelter data-attributten: data-track="true"
 * 3. Giv hvert felt et unikt name-attribut (f.eks. name="hvorfor_loow")
 * 4. Kald loowTracker.getReport() når formularen submittes for at få tracking-data
 * 5. Send tracking-data med til Notion API som skjulte felter
 * 
 * EKSEMPEL PÅ HTML:
 * <textarea name="hvorfor_loow" data-track="true" placeholder="Fortæl os kort..."></textarea>
 * <textarea name="god_kundeservice" data-track="true" placeholder="En kunde skriver..."></textarea>
 */

const loowTracker = (function () {

  // ─── STATE ───────────────────────────────────────────────────────────
  const pageLoadTime = Date.now();
  let tabSwitchCount = 0;

  // Per-felt tracking data
  // Struktur: { [feltNavn]: { keystrokes, pasteEvents, pasteChars, focusStart, totalFocusTime, contentLength } }
  const fieldData = {};

  // ─── HJÆLPEFUNKTIONER ────────────────────────────────────────────────

  function getFieldName(element) {
    return element.getAttribute('name') || element.getAttribute('id') || 'ukendt';
  }

  function ensureField(name) {
    if (!fieldData[name]) {
      fieldData[name] = {
        keystrokes: 0,          // Antal tastetryk (bogstaver, tal, tegn)
        pasteEvents: 0,         // Antal gange der er pastet
        pasteChars: 0,          // Samlet antal tegn indsat via paste
        focusStart: null,       // Tidspunkt for seneste focus
        totalFocusTime: 0,      // Samlet tid i feltet (ms)
        contentLength: 0,       // Antal tegn ved submit
        firstKeystrokeTime: null, // Tidspunkt for første tastetryk
        lastKeystrokeTime: null,  // Tidspunkt for seneste tastetryk
      };
    }
    return fieldData[name];
  }

  // ─── EVENT HANDLERS ──────────────────────────────────────────────────

  // Tæl tastetryk (kun tegn, ikke Shift/Ctrl/Alt osv.)
  function handleKeydown(e) {
    // Ignorér modifier-taster og navigationstaster
    if (e.key.length > 1 && !['Backspace', 'Delete', 'Enter'].includes(e.key)) return;
    // Ignorér Ctrl+V (det fanges af paste-event)
    if (e.ctrlKey || e.metaKey) return;

    const name = getFieldName(e.target);
    const field = ensureField(name);
    field.keystrokes++;

    const now = Date.now();
    if (!field.firstKeystrokeTime) field.firstKeystrokeTime = now;
    field.lastKeystrokeTime = now;
  }

  // Fang paste-events
  function handlePaste(e) {
    const name = getFieldName(e.target);
    const field = ensureField(name);

    // Hent indsat tekst fra clipboard
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
    field.pasteEvents++;
    field.pasteChars += pastedText.length;
  }

  // Track tid i hvert felt
  function handleFocus(e) {
    const name = getFieldName(e.target);
    const field = ensureField(name);
    field.focusStart = Date.now();
  }

  function handleBlur(e) {
    const name = getFieldName(e.target);
    const field = ensureField(name);
    if (field.focusStart) {
      field.totalFocusTime += Date.now() - field.focusStart;
      field.focusStart = null;
    }
    // Opdater indhold-længde
    field.contentLength = (e.target.value || '').length;
  }

  // Track tab/vindue-skift
  function handleVisibilityChange() {
    if (document.hidden) {
      tabSwitchCount++;
    }
  }

  // ─── INITIALISERING ──────────────────────────────────────────────────

  function init() {
    // Find alle felter med data-track="true"
    const trackedFields = document.querySelectorAll('[data-track="true"]');

    trackedFields.forEach(function (field) {
      field.addEventListener('keydown', handleKeydown);
      field.addEventListener('paste', handlePaste);
      field.addEventListener('focus', handleFocus);
      field.addEventListener('blur', handleBlur);
    });

    // Track tab-skift
    document.addEventListener('visibilitychange', handleVisibilityChange);

    console.log('[LOOW Tracker] Initialiseret — tracker ' + trackedFields.length + ' felter');
  }

  // ─── RAPPORT ─────────────────────────────────────────────────────────

  function getReport() {
    const now = Date.now();
    const totalSeconds = Math.round((now - pageLoadTime) / 1000);

    // Opdater contentLength og luk åbne focus-sessioner
    const trackedFields = document.querySelectorAll('[data-track="true"]');
    trackedFields.forEach(function (el) {
      const name = getFieldName(el);
      const field = ensureField(name);
      field.contentLength = (el.value || '').length;
      if (field.focusStart) {
        field.totalFocusTime += now - field.focusStart;
        field.focusStart = null;
      }
    });

    // Byg per-felt rapport
    const feltTiming = {};
    const pasteEvents = {};
    const keystrokeRatio = {};
    const typingSpeed = {};

    Object.keys(fieldData).forEach(function (name) {
      const d = fieldData[name];
      const focusSec = Math.round(d.totalFocusTime / 1000);

      feltTiming[name] = focusSec;

      pasteEvents[name] = {
        antal_paste: d.pasteEvents,
        tegn_pastet: d.pasteChars,
      };

      // Ratio: hvor stor en andel af indholdet er tastet vs. pastet
      const totalChars = d.contentLength || 1;
      const pasteRatio = Math.round((d.pasteChars / totalChars) * 100);
      keystrokeRatio[name] = {
        tegn_i_felt: d.contentLength,
        tegn_tastet: d.keystrokes,
        tegn_pastet: d.pasteChars,
        paste_procent: Math.min(pasteRatio, 100),
      };

      // Typing speed (tegn pr. minut) — kun baseret på tastede tegn
      let cpm = 0;
      if (d.firstKeystrokeTime && d.lastKeystrokeTime && d.keystrokes > 5) {
        const typingDurationMin = (d.lastKeystrokeTime - d.firstKeystrokeTime) / 60000;
        if (typingDurationMin > 0.05) { // mindst 3 sekunder
          cpm = Math.round(d.keystrokes / typingDurationMin);
        }
      }
      typingSpeed[name] = cpm;
    });

    return {
      _total_tid_sekunder: totalSeconds,
      _felt_timing_json: JSON.stringify(feltTiming),
      _paste_events_json: JSON.stringify(pasteEvents),
      _keystroke_ratio_json: JSON.stringify(keystrokeRatio),
      _typing_speed_json: JSON.stringify(typingSpeed),
      _tab_switches: tabSwitchCount,
    };
  }

  // ─── PUBLIC API ──────────────────────────────────────────────────────

  // Auto-init når DOM er klar
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return {
    getReport: getReport,
    getFieldData: function () { return fieldData; },  // til debugging
  };

})();


// ─── EKSEMPEL: BRUG VED FORM SUBMIT ─────────────────────────────────────
// 
// document.getElementById('loow-ansogning').addEventListener('submit', async function(e) {
//   e.preventDefault();
//   
//   // Hent tracking-data
//   const tracking = loowTracker.getReport();
//   
//   // Hent formular-data
//   const formData = new FormData(this);
//   
//   // Kombiner til Notion API payload
//   const payload = {
//     parent: { database_id: 'DIN_NOTION_DATABASE_ID' },
//     properties: {
//       // --- Synlige felter ---
//       'Navn':                { title: [{ text: { content: formData.get('navn') } }] },
//       'Email':               { email: formData.get('email') },
//       'Telefon':             { phone_number: formData.get('telefon') },
//       'Kender du LOOW?':     { select: { name: formData.get('kender_loow') } },
//       'Hvor bor du?':        { rich_text: [{ text: { content: formData.get('bopael') } }] },
//       'Erfaring':            { multi_select: JSON.parse(formData.get('erfaring') || '[]').map(v => ({ name: v })) },
//       'Kan starte 1. juni?': { select: { name: formData.get('kan_starte') } },
//       'Hvorfor LOOW?':       { rich_text: [{ text: { content: formData.get('hvorfor_loow') } }] },
//       'God kundeservice':    { rich_text: [{ text: { content: formData.get('god_kundeservice') } }] },
//       'En typisk dag':       { rich_text: [{ text: { content: formData.get('typisk_dag') } }] },
//       'Andet':               { rich_text: [{ text: { content: formData.get('andet') || '' } }] },
//       
//       // --- Skjulte tracking-felter ---
//       '_total_tid_sekunder':     { number: tracking._total_tid_sekunder },
//       '_felt_timing_json':       { rich_text: [{ text: { content: tracking._felt_timing_json } }] },
//       '_paste_events_json':      { rich_text: [{ text: { content: tracking._paste_events_json } }] },
//       '_keystroke_ratio_json':   { rich_text: [{ text: { content: tracking._keystroke_ratio_json } }] },
//       '_typing_speed_json':      { rich_text: [{ text: { content: tracking._typing_speed_json } }] },
//       '_tab_switches':           { number: tracking._tab_switches },
//     }
//   };
//   
//   // VIGTIGT: Kald IKKE Notion API direkte fra frontend (API-nøglen er hemmelig).
//   // Send i stedet til din egen backend/serverless function:
//   const response = await fetch('https://din-backend.com/api/ansogning', {
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify(payload),
//   });
//   
//   if (response.ok) {
//     // Vis bekræftelse
//   }
// });